import React from 'react';
import { Box, Button } from '@mui/material';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import { FilterSelect } from './FilterSelect';
import type { OptionItem } from './OptionList';

export interface FilterField {
  key: string;
  label: string;
  options: OptionItem[];
}

export interface FilterBarProps {
  fields: FilterField[];
  /** Field key to picked values. A key with no values, or no key, is unfiltered. */
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
  /**
   * Narrowest a filter gets before a row gives up a column, in px. The
   * default fits a long name such as "Prosper digital lead" and its value.
   */
  minFieldWidth?: number;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Every filter on a page, always visible, in one aligned grid.
 *
 * As many filters per row as the width allows, all the same width, so the
 * edges line up. "Clear all filters" appears only while something is
 * filtered, in the next free cell, so it never adds a row of its own.
 *
 * Controlled, and changes are reported at once; if each costs a request,
 * pair it with `useDebouncedCommit`.
 */
export function FilterBar({
  fields,
  value,
  onChange,
  minFieldWidth = 232,
  loading,
  disabled,
}: FilterBarProps) {
  const activeCount = Object.values(value).filter((v) => v && v.length).length;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${minFieldWidth}px, 100%), 1fr))`,
        alignItems: 'center',
      }}
    >
      {fields.map((field) => (
        <FilterSelect
          key={field.key}
          label={field.label}
          options={field.options}
          value={value[field.key] ?? []}
          onChange={(next) => onChange({ ...value, [field.key]: next })}
          loading={loading}
          disabled={disabled}
        />
      ))}

      {activeCount > 0 && (
        <Box>
          <Button
            size="small"
            startIcon={<RestartAltRounded />}
            onClick={() => onChange({})}
            disabled={disabled}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear all filters
          </Button>
        </Box>
      )}
    </Box>
  );
}
